import Button from 'carbon-react/lib/components/button';
import React, { useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Typography from 'carbon-react/lib/components/typography';
import { capitalize } from 'lodash';
import Box from 'carbon-react/lib/components/box';

import { ActivateProductSidebar } from '@futrli/billing';
import { FormattedMessage } from '@futrli/localisation';
import { getProductUrl, PREDICT_PRODUCT_SLUG, SfaFatalError } from '@futrli/shared-services';

import { trackCTA } from '../../core/tracker';
import { PredictManagementContext } from '../context/predict-management-context';
import image from './images/internal-error.svg';

/**
 * Trial expired page
 * @returns {JSX.Element}
 */
export const PredictTrialExpired: React.FC = (): JSX.Element => {
  const { organisationId: tempOrganisationId, workspaceId: tempWorkspaceId } = useParams();
  const organisationId = tempOrganisationId || '';
  const workspaceId = tempWorkspaceId || '';
  const { refetchOrganisation } = useContext(PredictManagementContext);
  const navigate = useNavigate();

  const onActivateSuccess = () => {
    refetchOrganisation?.();
    navigate(getProductUrl(workspaceId, organisationId, PREDICT_PRODUCT_SLUG));
  };

  const onClickCTA = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    const CTAText = 'Go Pro now';

    trackCTA(CTAText, CTAText);
  };

  return (
    <SfaFatalError
      image={image}
      title={
        <FormattedMessage
          id="product.trial_expired"
          defaultMessage="{PRODUCT} trial expired"
          values={{ PRODUCT: capitalize(PREDICT_PRODUCT_SLUG) }}
        />
      }
      subtitle={
        <FormattedMessage
          id="predict.trial_expired.paragraph_1"
          defaultMessage="There’s so much to help you manage your cash flow:"
        />
      }
      text={
        <>
          <Box>
            <Typography variant="ul">
              <Typography as="li">
                <FormattedMessage
                  id="predict.trial_expired.option_1"
                  defaultMessage="Predicted cash flow using a bespoke prediction AI"
                />
              </Typography>
              <Typography as="li">
                <FormattedMessage
                  id="predict.trial_expired.option_2"
                  defaultMessage="AI and manual predictions on your accounts"
                />
              </Typography>
              <Typography as="li">
                <FormattedMessage
                  id="predict.trial_expired.option_3"
                  defaultMessage="See and action incoming invoices and bills"
                />
              </Typography>
              <Typography as="li">
                <FormattedMessage
                  id="predict.trial_expired.option_4"
                  defaultMessage="Full visibility on all your financials"
                />
              </Typography>
            </Typography>
            <Typography>
              <FormattedMessage
                id="predict.trial_expired.paragraph_2"
                defaultMessage="Get unlimited access to Predict."
              />
            </Typography>
          </Box>

          <div>
            <ActivateProductSidebar
              productSlug={PREDICT_PRODUCT_SLUG}
              workspaceId={workspaceId as string}
              organisationId={organisationId as string}
              wrapperComponent="span"
              onSuccess={onActivateSuccess}
            >
              <Button buttonType="primary" size="large" onClick={onClickCTA}>
                <FormattedMessage id="billing.go_pro_now" defaultMessage="Go Pro now" />
              </Button>
            </ActivateProductSidebar>
          </div>
        </>
      }
    />
  );
};
